import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/design-system/button';

import { warehouseApi } from '../../api/warehouse.api';
import { GoodsReceiptTable } from '../../components/GoodsReceiptTable';
import { ListSearch, ListShell } from '../../components/ListShell';
import { HeaderActions } from '../../components/pickers';
import { LoadError, PageHeader } from '../../components/ui';
import { WarehouseFilterPanel } from '../../components/WarehouseFilterPanel';
import { GOODS_RECEIPT_FILTERS } from '../../filterFields';
import { useListQuery } from '../../hooks/useListQuery';
import { useLoad } from '../../hooks/useLoad';
import { useWarehouse } from '../../WarehouseContext';
import { WAREHOUSE_PATHS } from '../../paths';

const TABS = ['ALL', 'POSTED'] as const;
type Tab = (typeof TABS)[number];

const GoodsReceiptListPage: React.FC = () => {
  const navigate = useNavigate();
  const { hubID, ready, refreshToken } = useWarehouse();
  const q = useListQuery<Tab>(TABS, 'ALL', GOODS_RECEIPT_FILTERS);
  const { api: filterApi, apiKey: filterKey } = q.filters;

  // No summary endpoint: the tab count is this filtered list's total.
  const list = useLoad(
    (signal) =>
      warehouseApi.listGoodsReceipts(
        {
          hubID,
          ...filterApi,
          search: q.search.committed || null,
          page: q.page,
          limit: q.limit,
        },
        { signal }
      ),
    [hubID, filterKey, q.search.committed, q.page, q.limit, refreshToken],
    ready
  );
  const total = list.data?.pagination.total ?? null;

  return (
    <div className="space-y-5 p-6">
      <PageHeader
        eyebrow="Warehouse · Inbound management"
        title="Goods receipt"
        actions={
          <HeaderActions>
            <Button
              onClick={() => navigate(`${WAREHOUSE_PATHS.goodsReceipt}/new`)}
              leftIcon={<i className="ri-add-line text-[14px]" />}
            >
              Create goods receipt
            </Button>
          </HeaderActions>
        }
      />
      <ListShell
        // GR v2 is created-and-posted in one step, so "Posted" == "All".
        tabs={[
          { key: 'ALL', label: 'All', count: total },
          { key: 'POSTED', label: 'Posted', count: total },
        ]}
        activeTab={q.tab}
        onTabChange={q.setTab}
        filters={
          <>
            <ListSearch
              value={q.search.value}
              onChange={q.search.setValue}
              placeholder="Search GR, PO, client…"
            />
            <WarehouseFilterPanel
              filters={q.filters}
              title="Filter goods receipt"
            />
          </>
        }
        pagination={{
          page: q.page,
          limit: q.limit,
          total: total ?? 0,
          onPageChange: q.setPage,
          onLimitChange: q.setLimit,
        }}
      >
        {list.error ? (
          <LoadError message={list.error.message} onRetry={list.reload} />
        ) : (
          <GoodsReceiptTable
            rows={list.data?.data ?? []}
            loading={list.loading || !ready}
          />
        )}
      </ListShell>
    </div>
  );
};

export default GoodsReceiptListPage;
